/**
 * useArTableCsvImport Hook
 *
 * Orchestrates CSV import: parsing and sequential row creation via
 * ArTableActionsContext.addRow. Entity references use manual entry mode.
 *
 * Consumed by `CsvImportModal`. The layout element's `columnConfig` is passed to
 * `flattenColumnsForCsv` raw — `undefined` is every schema column, `[]` is none,
 * which leaves `flatColumns` empty and lets no CSV parse (SYD-2279).
 */

import { useState, useContext, useCallback, useMemo } from 'react';
import { snapshot } from 'valtio';
import { FieldTypeContext } from '@/providers/FieldTypeContext';
import { useStoreNonReactive } from '@/store/useStoreNonReactive';
import { useCompositeFieldTypes } from '@/components/CompositeField/useCompositeFieldTypes';
import { ArTableActionsContext } from '../context';
import { flattenColumnsForCsv } from './flattenColumnsForCsv';
import { parseCsvImport } from './parseCsvImport';
import type { CsvFlatColumn } from './flattenColumnsForCsv';
import type { CsvParseResult, CsvImportError } from './parseCsvImport';
import type { ArTableType } from '#types/System.Artifacts.types';
import type { UIArTableColumnOverride } from '#types/System.types';
import type { DocumentState } from '@/store/Store.types';
import type { Snapshot } from 'valtio';

type ImportProgress = {
	current: number;
	total: number;
};

type ImportResult = {
	addedCount: number;
	errorCount: number;
	errors: CsvImportError[];
};

type UseArTableCsvImportProps = {
	columnConfig?: UIArTableColumnOverride[];
};

type UseArTableCsvImportReturn = {
	flatColumns: CsvFlatColumn[];
	parseResult: CsvParseResult | null;
	resetParseResult: () => void;
	parseCsv: (csvText: string) => CsvParseResult;
	importRows: () => Promise<ImportResult>;
	importing: boolean;
	progress: ImportProgress;
};

function useArTableCsvImport({ columnConfig }: UseArTableCsvImportProps): UseArTableCsvImportReturn {
	const [parseResult, setParseResult] = useState<CsvParseResult | null>(null);
	const [importing, setImporting] = useState(false);
	const [progress, setProgress] = useState<ImportProgress>({ current: 0, total: 0 });

	const { fieldTypes } = useContext(FieldTypeContext);
	const actions = useContext(ArTableActionsContext);
	const { state: schemaState } = useStoreNonReactive('schema');
	const compositeFieldTypes = useCompositeFieldTypes();

	const getSchema = useCallback((): ArTableType | null => {
		const schemaSnap = snapshot(schemaState) as Snapshot<DocumentState<ArTableType>>;
		return (schemaSnap.data as ArTableType) ?? null;
	}, [schemaState]);

	const flatColumns = useMemo(() => {
		const schema = getSchema();
		if (!schema || compositeFieldTypes.length === 0) return [];
		return flattenColumnsForCsv(schema, fieldTypes, columnConfig, compositeFieldTypes);
	}, [getSchema, fieldTypes, columnConfig, compositeFieldTypes]);

	const resetParseResult = useCallback(() => {
		setParseResult(null);
	}, []);

	const parseCsv = useCallback((csvText: string): CsvParseResult => {
		if (flatColumns.length === 0) {
			const empty: CsvParseResult = { rows: [], errors: [], warnings: ['Table schema not loaded'], totalRows: 0 };
			setParseResult(empty);
			return empty;
		}

		const result = parseCsvImport(csvText, flatColumns);
		setParseResult(result);
		return result;
	}, [flatColumns]);

	const importRows = useCallback(async (): Promise<ImportResult> => {
		if (!parseResult || parseResult.rows.length === 0 || !actions) {
			return { addedCount: 0, errorCount: 0, errors: [] };
		}

		setImporting(true);
		setProgress({ current: 0, total: parseResult.rows.length });

		try {
			const allErrors = [...parseResult.errors];
			const rowErrors: CsvImportError[] = [];
			const rows = parseResult.rows;
			let addedCount = 0;

			for (let i = 0; i < rows.length; i++) {
				const cells = rows[i];
				try {
					const rowId = await actions.addRow(cells);
					if (rowId) {
						addedCount++;
					} else {
						rowErrors.push({ row: i + 2, column: '', message: 'Failed to add row' });
					}
				} catch (error) {
					rowErrors.push({
						row: i + 2,
						column: '',
						message: error instanceof Error ? error.message : 'Unknown error',
					});
				}
				setProgress({ current: i + 1, total: rows.length });
			}

			setParseResult(null);

			return {
				addedCount,
				errorCount: allErrors.length + rowErrors.length,
				errors: [...allErrors, ...rowErrors],
			};
		} finally {
			setImporting(false);
		}
	}, [parseResult, actions]);

	return { flatColumns, parseResult, resetParseResult, parseCsv, importRows, importing, progress };
}

export { useArTableCsvImport };
export type { ImportProgress, ImportResult, UseArTableCsvImportReturn };
